import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { eventBus } from "../../utils/eventBus";
import { StatusScreen } from "./Chrome";
import {
  canFullscreen,
  checkPin,
  enterFullscreen,
  isStaffSessionLocked,
  leaveFullscreen,
  lockStaffSession,
  pinWaitSeconds,
  pushHistoryGuard,
  unlockStaffSession,
} from "./kioskLock";
import "../forms.css";
import "./patient.css";

// Tablet ("kiosk") mode on a staff device. The staff UI (W3) emits
//   eventBus "formwiz:kiosk" {session, requestId, patientName, practiceName}
// and this host, mounted once in App for signed-in routes, shows a
// fullscreen overlay: staff hand-off screen (set a 4-digit PIN, Start) ->
// the patient fills the forms (PatientFormsSession, kiosk) -> every exit
// (the corner button, Escape, Close on the done screen) asks for the PIN.
// Unlocking closes the overlay and emits "formwiz:kiosk-closed" {requestId}.
//
// Nothing of the staff app is reachable while it is open: the overlay is
// portalled to <body>, the app root is made inert + aria-hidden, the body
// does not scroll, and on Start the overlay itself goes fullscreen (where
// the browser allows), so only it is painted.
//
// On Start the staff sign-in also leaves the device's storage (kioskLock.js):
// the token is held in memory for this page only, so a reload, a new tab or
// another window lands signed out (a reload shows "return the device to the
// staff", App.js), and only the right PIN puts it back. The PIN is held in
// memory by kioskLock too, so the lock survives this component remounting
// (a route change behind the overlay); wrong PINs back off. Desktop browsers
// also ask "Leave site?" before a reload. Back (the iPad edge swipe) stays on
// this page (a history guard entry, re-pushed while the patient holds the
// device), and an earlier staff page restored from the back/forward cache
// reloads into the locked screen (kioskLock installKioskPageGuard, App.js).
// Fullscreen uses the WebKit API where the standard one is missing; iPhone
// Safari has no element fullscreen at all, so the hand-off screen tells staff
// to use Guided Access. After KIOSK_IDLE_MS without a touch the form is hidden
// behind a bilingual "Are you still there?" screen (answers are kept).
// SOP: docs/forms/TABLET_MODE.md.

export const KIOSK_EVENT = "formwiz:kiosk";
export const KIOSK_CLOSED_EVENT = "formwiz:kiosk-closed";
export const KIOSK_IDLE_MS = 3 * 60 * 1000;
const IDLE_EVENTS = ["pointerdown", "keydown", "touchstart", "wheel", "input", "scroll"];

const PatientFormsSession = lazy(() => import("./PatientFormsSession"));

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "⌫"];

function PinPad({ value, onChange, onDone, error, label, autoFocus }) {
  const inputRef = useRef(null);
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      try { inputRef.current.focus({ preventScroll: true }); } catch (_) { inputRef.current.focus(); }
    }
  }, [autoFocus]);
  const press = (k) => {
    if (k === "⌫") onChange(value.slice(0, -1));
    else if (k) onChange((value + k).slice(0, 4));
  };
  return (
    <div className="bcf-pin">
      <label className="bcf-sr" htmlFor="bcf-kiosk-pin">{label}</label>
      <input
        ref={inputRef}
        id="bcf-kiosk-pin"
        className="bcf-pin-input"
        type="password"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="off"
        maxLength={4}
        value={value}
        aria-invalid={!!error}
        aria-describedby={error ? "bcf-pin-err" : undefined}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 4))}
        onKeyDown={(e) => { if (e.key === "Enter" && value.length === 4) onDone(); }}
      />
      <div className="bcf-pin-dots" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => <span key={i} className={i < value.length ? "is-on" : ""} />)}
      </div>
      <div className="bcf-pin-err" id="bcf-pin-err" role={error ? "alert" : undefined}>{error || " "}</div>
      <div className="bcf-pin-keys">
        {KEYS.map((k, i) => (k === "" ? <span key={i} /> : (
          <button key={i} type="button" className="bcf-pin-key" onClick={() => press(k)} aria-label={k === "⌫" ? "Delete" : k}>{k}</button>
        )))}
      </div>
    </div>
  );
}

// Mounted while tablet mode is still locked (the overlay was unmounted behind
// a route change and came back): no patient session to show, only "return
// the device" and the PIN.
const RELOCKED = { session: null, requestId: null, patientName: "", practiceName: "", relocked: true };

const pinWaitText = (sec) => `Too many tries. Wait ${sec} second${sec === 1 ? "" : "s"}.`;

export default function FormKioskHost() {
  const [kiosk, setKiosk] = useState(() => (isStaffSessionLocked() ? RELOCKED : null)); // event payload
  const [stage, setStage] = useState(() => (isStaffSessionLocked() ? "patient" : "handoff")); // handoff | patient
  const [unlocking, setUnlocking] = useState(false);
  const [ended, setEnded] = useState(() => isStaffSessionLocked());
  const [entry, setEntry] = useState("");
  const [pinError, setPinError] = useState("");
  const [wait, setWait] = useState(0);
  const [tick, setTick] = useState(0);
  const [idle, setIdle] = useState(false);
  const [fullscreenOk] = useState(() => canFullscreen());
  const rootRef = useRef(null);
  const scrollRef = useRef(null);
  const activeRef = useRef(isStaffSessionLocked());

  useEffect(() => {
    const onKiosk = (payload) => {
      if (activeRef.current || !payload || !payload.session) return;
      activeRef.current = true;
      setKiosk({
        session: String(payload.session),
        requestId: payload.requestId,
        patientName: payload.patientName || "",
        practiceName: payload.practiceName || "",
      });
      setStage("handoff");
      setUnlocking(false);
      setEnded(false);
      setIdle(false);
      setEntry("");
      setPinError("");
    };
    eventBus.on(KIOSK_EVENT, onKiosk);
    return () => { eventBus.off(KIOSK_EVENT, onKiosk); };
  }, []);

  const finish = useCallback(() => {
    const requestId = kiosk && kiosk.requestId;
    unlockStaffSession(); // the staff sign-in goes back to storage
    leaveFullscreen();
    activeRef.current = false;
    setKiosk(null);
    setStage("handoff");
    setUnlocking(false);
    setEnded(false);
    setIdle(false);
    setEntry("");
    setPinError("");
    setWait(0);
    eventBus.emit(KIOSK_CLOSED_EVENT, { requestId });
  }, [kiosk]);

  // While open: the staff app is inert and hidden from assistive tech, the
  // body does not scroll, and leaving the page asks first.
  useEffect(() => {
    if (!kiosk) return undefined;
    const appRoot = document.getElementById("root");
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (appRoot) {
      appRoot.setAttribute("inert", "");
      appRoot.setAttribute("aria-hidden", "true");
    }
    const beforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => {
      document.body.style.overflow = prevOverflow;
      if (appRoot) {
        appRoot.removeAttribute("inert");
        appRoot.removeAttribute("aria-hidden");
      }
      window.removeEventListener("beforeunload", beforeUnload);
    };
  }, [kiosk]);

  // The renderer scrolls the window to the top on every step; inside the
  // overlay the scroller is the overlay, so window.scrollTo is pointed at it
  // while the patient is filling.
  useEffect(() => {
    if (!kiosk || stage !== "patient") return undefined;
    const original = window.scrollTo;
    window.scrollTo = (...args) => {
      const el = scrollRef.current;
      if (!el) return;
      try {
        if (typeof args[0] === "object" && args[0]) el.scrollTo({ top: args[0].top || 0, left: 0 });
        else el.scrollTop = Number(args[1]) || 0;
      } catch (_) {
        el.scrollTop = 0;
      }
    };
    return () => { window.scrollTo = original; };
  }, [kiosk, stage]);

  const askPin = useCallback(() => {
    setEntry("");
    const w = pinWaitSeconds();
    setWait(w);
    setPinError(w ? pinWaitText(w) : "");
    setUnlocking(true);
  }, []);

  // Back-off countdown on the PIN screen.
  useEffect(() => {
    if (!unlocking || wait <= 0) return undefined;
    const h = setTimeout(() => {
      const w = pinWaitSeconds();
      setWait(w);
      setPinError(w ? pinWaitText(w) : "");
      setTick((n) => n + 1);
    }, 1000);
    return () => clearTimeout(h);
  }, [unlocking, wait, tick]);

  // Back (browser button, iPad edge swipe) while the patient holds the
  // device: stay here. The guard entry pushed on Start was used up by this
  // Back; push it again. (If a browser skips the guard and leaves the page,
  // the restored/reloaded staff page boots into the locked screen.)
  useEffect(() => {
    if (!kiosk || stage !== "patient") return undefined;
    const onPop = () => { pushHistoryGuard(); };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [kiosk, stage]);

  // Idle: no touch for KIOSK_IDLE_MS while the form is on screen hides it
  // behind "Are you still there?" (a patient who walks away does not leave
  // their answers on a front-desk tablet). The form stays mounted.
  useEffect(() => {
    if (!kiosk || stage !== "patient" || ended || unlocking || idle) return undefined;
    const el = rootRef.current;
    let h = null;
    const arm = () => {
      clearTimeout(h);
      h = setTimeout(() => setIdle(true), KIOSK_IDLE_MS);
    };
    if (el) IDLE_EVENTS.forEach((t) => el.addEventListener(t, arm, true));
    arm();
    return () => {
      clearTimeout(h);
      if (el) IDLE_EVENTS.forEach((t) => el.removeEventListener(t, arm, true));
    };
  }, [kiosk, stage, ended, unlocking, idle]);

  // Escape (outside the PIN screen) asks for the PIN; never closes by itself.
  useEffect(() => {
    if (!kiosk || stage !== "patient") return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !unlocking) {
        e.preventDefault();
        askPin();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [kiosk, stage, unlocking, askPin]);

  const start = () => {
    if (entry.length !== 4) {
      setPinError("Enter 4 digits.");
      return;
    }
    lockStaffSession(entry); // staff sign-in out of storage; PIN held in memory
    setEntry("");
    setPinError("");
    setIdle(false);
    setStage("patient");
    pushHistoryGuard(); // in the Start tap: Back stays on this page
    // Standard or WebKit element fullscreen; where neither exists (iPhone)
    // or it is refused, the overlay still covers the app (hand-off note).
    enterFullscreen(rootRef.current);
  };

  const unlock = () => {
    if (entry.length !== 4 || wait > 0) return;
    const r = checkPin(entry);
    if (r.ok) {
      finish();
      return;
    }
    setEntry("");
    setWait(r.waitSeconds || 0);
    setPinError(r.waitSeconds ? pinWaitText(r.waitSeconds) : "Incorrect PIN — try again.");
  };

  if (!kiosk) return null;

  const overlay = (
    <div className="bcf-kiosk" ref={rootRef} role="dialog" aria-modal="true" aria-label="Tablet mode" data-kiosk-stage={stage}>
      {stage === "handoff" ? (
        <div className="bcf bcf-kiosk-staff">
          <div className="bcf-kiosk-card">
            <div className="bcf-kiosk-eyebrow">Tablet mode</div>
            <h1 className="bcf-kiosk-title">Hand the device to {kiosk.patientName || "the patient"}</h1>
            <p className="bcf-kiosk-sub">Set a 4-digit staff PIN. You will need it to exit tablet mode.</p>
            <PinPad value={entry} onChange={(v) => { setPinError(""); setEntry(v); }} onDone={start} error={pinError} label="Staff PIN" autoFocus />
            <div className="bcf-kiosk-actions">
              <button type="button" className="bcf-btn bcf-btn-ghost" onClick={finish}>Cancel</button>
              <button type="button" className="bcf-btn bcf-btn-primary" onClick={start} disabled={entry.length !== 4}>Start</button>
            </div>
            {fullscreenOk ? (
              <p className="bcf-kiosk-tip" data-testid="kiosk-tip">
                After Start, turn on Guided Access (iPad) or screen pinning (Android) so the patient stays in this screen.
              </p>
            ) : (
              <p className="bcf-kiosk-tip is-warn" role="note" data-testid="kiosk-tip">
                This browser cannot lock the screen to the form (no fullscreen, e.g. iPhone). Turn on Guided Access
                (Settings &gt; Accessibility &gt; Guided Access) after Start, close other tabs, and stay nearby.
              </p>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="bcf-kiosk-bar">
            <span>BrainyCare forms</span>
            <button type="button" className="bcf-kiosk-exit" onClick={askPin} aria-label="Exit tablet mode (staff PIN)">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
            </button>
          </div>
          <div className="bcf-kiosk-scroll" ref={scrollRef} style={idle ? { visibility: "hidden" } : undefined} aria-hidden={idle ? "true" : undefined}>
            {ended ? (
              <StatusScreen kind="kioskEnded" practice={{ name: kiosk.practiceName }} lang="en" hideContact bilingual>
                <div className="bcf-status-actions">
                  <button type="button" className="bcf-btn bcf-btn-primary" onClick={askPin}>Close</button>
                </div>
              </StatusScreen>
            ) : (
              <Suspense fallback={null}>
                <PatientFormsSession
                  session={kiosk.session}
                  kiosk
                  onKioskClose={askPin}
                  onSessionLost={() => setEnded(true)}
                />
              </Suspense>
            )}
          </div>
          {idle && !unlocking && (
            <div className="bcf bcf-kiosk-lock bcf-kiosk-idle" role="alertdialog" aria-modal="true" aria-labelledby="bcf-kiosk-idle-title">
              <div className="bcf-kiosk-card is-compact">
                <h2 className="bcf-kiosk-title" id="bcf-kiosk-idle-title">Are you still there?</h2>
                <p className="bcf-kiosk-title is-alt" lang="es">¿Sigue ahí?</p>
                <p className="bcf-kiosk-sub">Your answers are saved.</p>
                <p className="bcf-kiosk-sub" lang="es">Sus respuestas están guardadas.</p>
                <div className="bcf-kiosk-actions">
                  <button type="button" className="bcf-btn bcf-btn-primary" onClick={() => setIdle(false)}>Continue / Continuar</button>
                </div>
              </div>
            </div>
          )}
          {unlocking && (
            <div className="bcf bcf-kiosk-lock">
              <div className="bcf-kiosk-card is-compact">
                <h2 className="bcf-kiosk-title">Staff PIN</h2>
                <p className="bcf-kiosk-sub" lang="es">Solo personal. Por favor devuelva el dispositivo.</p>
                <PinPad value={entry} onChange={(v) => { setPinError(""); setEntry(v); }} onDone={unlock} error={pinError} label="Staff PIN" autoFocus />
                <div className="bcf-kiosk-actions">
                  <button type="button" className="bcf-btn bcf-btn-ghost" onClick={() => { setUnlocking(false); setEntry(""); setPinError(""); }}>Cancel</button>
                  <button type="button" className="bcf-btn bcf-btn-primary" onClick={unlock} disabled={entry.length !== 4 || wait > 0}>Unlock</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  return typeof document !== "undefined" && document.body ? createPortal(overlay, document.body) : overlay;
}

// After a reload during tablet mode (kioskLock marker in this tab's
// sessionStorage) the staff sign-in is gone. App.js shows this instead of the
// staff app or the sign-in form; staff continue to sign-in from here.
export function KioskRelockScreen({ onStaffSignIn }) {
  return (
    <div className="bcf-kiosk" role="dialog" aria-modal="true" aria-label="Tablet mode ended" data-kiosk-stage="relock">
      <div className="bcf-kiosk-scroll">
        <StatusScreen kind="kioskEnded" practice={{ name: "" }} lang="en" hideContact bilingual>
          <div className="bcf-status-actions">
            <button type="button" className="bcf-btn bcf-btn-ghost" onClick={onStaffSignIn}>Staff sign-in</button>
          </div>
        </StatusScreen>
      </div>
    </div>
  );
}
